import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { computeSession, SessionType, Platform, TournamentEntry, StakeInfo } from '../store';
import { useT } from '../lib/i18n';
import { useAuth } from '../hooks/useAuth';
import { useDB } from '../lib/useDB';


interface Props { onSaved: () => void; }

const PLATFORMS: Platform[] = ['GGPoker', 'PokerStars', 'PartyPoker', 'WPN', 'Live', 'Other'];

function emptyTournament(): TournamentEntry {
  return { id: uuidv4(), name: '', format: 'MTT', buyIn: 0, rebuys: 0, addon: 0, prize: 0, bountiesWon: 0, finishPosition: null, totalEntrants: null, notes: '' };
}

export function SessionForm({ onSaved }: Props) {
  const { user } = useAuth();
  const { saveSession } = useDB(user);

  const { t } = useT();

  const [type, setType]         = useState<SessionType>('tournament');
  const [date, setDate]         = useState(new Date().toISOString().split('T')[0]);
  const [platform, setPlatform] = useState<Platform>('GGPoker');
  const [notes, setNotes]       = useState('');
  const [duration, setDuration] = useState('');
  const [saved, setSaved]       = useState(false);
  const [stakes, setStakes]     = useState('');
  const [buyIn, setBuyIn]       = useState('');
  const [cashOut, setCashOut]   = useState('');
  const [tournaments, setTournaments] = useState<TournamentEntry[]>([emptyTournament()]);
  const [staking, setStaking]   = useState<StakeInfo>({ isStaked: false, stakerName: '', myPercent: 50 });

  const updateTournament = (idx: number, field: keyof TournamentEntry, value: any) =>
    setTournaments(prev => prev.map((tr, i) => i === idx ? { ...tr, [field]: value } : tr));

  const previewProfit = () => {
    if (type === 'cash') return (parseFloat(cashOut) || 0) - (parseFloat(buyIn) || 0);
    return tournaments.reduce((acc, tr) =>
      acc + (tr.prize + tr.bountiesWon - (tr.buyIn + tr.rebuys * tr.buyIn + tr.addon)),
      0);
  };

  const gross = previewProfit();
  const net = staking.isStaked ? gross * (staking.myPercent / 100) : gross;

  const handleSave = () => {
    const base = {
      id: uuidv4(), date, type, platform, notes, duration: duration ? parseInt(duration) : null, staking,
      stakes: type === 'cash' ? stakes : '', buyIn: type === 'cash' ? parseFloat(buyIn) || 0 : 0,
      cashOut: type === 'cash' ? parseFloat(cashOut) || 0 : 0, tournaments: type === 'cash' ? [] : tournaments, tags: []
    };
    saveSession(computeSession(base));
    setSaved(true);
    setTimeout(() => { setSaved(false); onSaved(); }, 1200);
  };

  const canSave = type === 'cash' ? (parseFloat(buyIn) >= 0 && parseFloat(cashOut) >= 0) : tournaments.every(tr => tr.buyIn >= 0);

  const typeLabels: Record<SessionType, string> = {
    cash: t('session.typeCash'),
    tournament: t('session.typeTournament'),
    multi: t('session.typeMulti'),
  };

  return (
    <div className="session-form">
      <div className="form-header">
        <h2 className="form-title">{t('session.title')}</h2>
        <div className="type-selector">
          {(['cash', 'tournament', 'multi'] as SessionType[]).map(tp => (
            <button key={tp} className={`type-btn ${type === tp ? 'active' : ''}`}
              onClick={() => { setType(tp); if (tp === 'multi' && tournaments.length === 1) setTournaments([emptyTournament(), emptyTournament()]); }}>
              {typeLabels[tp]}
            </button>
          ))}
        </div>
      </div>

      <div className="form-body">
        <div className="form-col">
          <div className="form-section-title">{t('session.goal')}</div>
          <Field label={t('session.date')}>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </Field>
          <Field label={t('session.platform')}>
            <select value={platform} onChange={e => setPlatform(e.target.value as Platform)}>
              {PLATFORMS.map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label={t('session.duration')}>
            <input type="number" placeholder="—" value={duration} onChange={e => setDuration(e.target.value)} />
          </Field>
          <Field label={t('session.notes')}>
            <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('session.notesPlaceholder')} />
          </Field>
          <div className="staking-toggle">
            <label className="toggle-label">
              <input type="checkbox" checked={staking.isStaked} onChange={e => setStaking(s => ({ ...s, isStaked: e.target.checked }))} />
              <span>{t('session.staked')}</span>
            </label>
          </div>
          {staking.isStaked && (
            <div className="staking-fields">
              <Field label={t('session.stakerName')}>
                <input type="text" value={staking.stakerName} onChange={e => setStaking(s => ({ ...s, stakerName: e.target.value }))} />
              </Field>
              <Field label={`${t('session.myPct')} (${staking.myPercent}%)`}>
                <input type="range" min="1" max="99" value={staking.myPercent} onChange={e => setStaking(s => ({ ...s, myPercent: parseInt(e.target.value) }))} />
              </Field>
              <div className="staking-split-preview">
                <span>Yo: {staking.myPercent}%</span>
                <span>Backer: {100 - staking.myPercent}%</span>
              </div>
            </div>
          )}
        </div>

        <div className="form-col form-col-main">
          {type === 'cash' ? (
            <>
              <div className="form-section-title">{t('session.typeCash')}</div>
              <Field label={t('session.stakes')}><input type="text" value={stakes} onChange={e => setStakes(e.target.value)} placeholder={t('session.stakesPlaceholder')} /></Field>
              <Field label={t('session.buyIn')}><input type="number" min="0" value={buyIn} onChange={e => setBuyIn(e.target.value)} placeholder="0.00" /></Field>
              <Field label={t('session.cashOut')}><input type="number" min="0" value={cashOut} onChange={e => setCashOut(e.target.value)} placeholder="0.00" /></Field>
            </>
          ) : (
            <div className="tournaments-section">
              <div className="form-section-title">
                {type === 'multi' ? t('session.typeMulti') : t('session.typeTournament')}
                {type === 'multi' && <button className="btn-add-tourney" onClick={() => setTournaments(prev => [...prev, emptyTournament()])}>+ {t('session.addTournament')}</button>}
              </div>
              {tournaments.map((tr, idx) => (
                <TournamentForm key={tr.id} t={tr} idx={idx} canRemove={tournaments.length > 1}
                  onUpdate={updateTournament}
                  onRemove={(i) => setTournaments(prev => prev.filter((_, ix) => ix !== i))}
                  labels={{ name: t('session.tournamentName'), buyIn: t('session.tournamentBuyIn'), rebuys: t('session.rebuys'), addOn: t('session.addOn'), prize: t('session.prize'), bounties: t('session.bounties'), position: t('session.position'), entrants: t('session.entrants'), format: t('session.format') }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="form-footer">
        <div className="profit-preview-block">
          <div className="preview-row">
            <span>{t('session.grossProfit')}</span>
            <span className={gross >= 0 ? 'pos' : 'neg'}>{gross >= 0 ? '+' : ''}${gross.toFixed(2)}</span>
          </div>
          {staking.isStaked && (
            <div className="preview-row">
              <span>{t('session.myPct')} ({staking.myPercent}%)</span>
              <span className={net >= 0 ? 'pos' : 'neg'}>{net >= 0 ? '+' : ''}${net.toFixed(2)}</span>
            </div>
          )}
        </div>
        <button className={`btn btn-primary btn-save ${saved ? 'saved' : ''}`} disabled={!canSave || saved} onClick={handleSave}>
          {saved ? t('session.saved') : t('session.save')}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="form-field"><label className="field-label">{label}</label>{children}</div>;
}

function TField({ label, value, onChange }: { label: string; value: any; onChange: (v: string) => void }) {
  return <div className="tfield"><label>{label}</label><input type="number" value={value} onChange={e => onChange(e.target.value)} /></div>;
}

function TournamentForm({ t: tr, idx, canRemove, onUpdate, onRemove, labels }: {
  t: TournamentEntry; idx: number; canRemove: boolean;
  onUpdate: (i: number, f: keyof TournamentEntry, v: any) => void;
  onRemove: (i: number) => void;
  labels: Record<string, string>;
}) {
  const num = (s: string) => parseFloat(s) || 0;
  const profit = (tr.prize + tr.bountiesWon) - (tr.buyIn + tr.rebuys * tr.buyIn + tr.addon);
  return (
    <div className="tournament-card">
      <div className="tcard-header">
        <span className="tcard-num">#{idx + 1}</span>
        <input className="tcard-name" type="text" placeholder={labels.name} value={tr.name} onChange={e => onUpdate(idx, 'name', e.target.value)} />
        <select value={tr.format} onChange={e => onUpdate(idx, 'format', e.target.value)}>
          {['MTT', 'SNG', 'Bounty', 'AoF', 'Satellite', 'Spin', 'Other'].map(f => <option key={f}>{f}</option>)}
        </select>
        {canRemove && <button className="tcard-remove" onClick={() => onRemove(idx)}>✕</button>}
      </div>
      <div className="tcard-body">
        <div className="tcard-row">
          <TField label={labels.buyIn}     value={tr.buyIn}      onChange={v => onUpdate(idx, 'buyIn', num(v))} />
          <TField label={labels.rebuys}    value={tr.rebuys}     onChange={v => onUpdate(idx, 'rebuys', parseInt(v) || 0)} />
          <TField label={labels.addOn}     value={tr.addon}      onChange={v => onUpdate(idx, 'addon', num(v))} />
        </div>
        <div className="tcard-row">
          <TField label={labels.prize}     value={tr.prize}      onChange={v => onUpdate(idx, 'prize', num(v))} />
          <TField label={labels.bounties}  value={tr.bountiesWon} onChange={v => onUpdate(idx, 'bountiesWon', num(v))} />
          <TField label={labels.position}  value={tr.finishPosition ?? ''} onChange={v => onUpdate(idx, 'finishPosition', parseInt(v) || null)} />
          <TField label={labels.entrants}  value={tr.totalEntrants ?? ''} onChange={v => onUpdate(idx, 'totalEntrants', parseInt(v) || null)} />
        </div>
      </div>
      <div className={`tcard-result ${profit >= 0 ? 'pos' : 'neg'}`}>{profit >= 0 ? '+' : ''}${profit.toFixed(2)}</div>
    </div>
  );
}
